import React, { useState } from 'react';
import { Check, FileText, Plus } from 'lucide-react';
//import { useNavigate } from 'react-router-dom'; 

const PricingCard = ({ tier, price, subtext, features, isPopular, theme, action, billingCycle, expansionText }) => (
  <div style={{ 
    padding: '40px', 
    borderRadius: '16px', 
    background: 'white', 
    border: isPopular ? `2px solid ${theme.primary}` : '1px solid #e2e8f0',
    boxShadow: isPopular ? '0 25px 50px -12px rgba(37, 99, 235, 0.25)' : '0 10px 30px -5px rgba(0,0,0,0.05)',
    position: 'relative', 
    display: 'flex', 
    flexDirection: 'column',
    transition: 'transform 0.2s ease',
    transform: isPopular ? 'scale(1.02)' : 'none',
    zIndex: isPopular ? 10 : 1
  }}>
    {isPopular && (
      <span style={{ 
        position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', 
        background: theme.primary, color: 'white', padding: '4px 12px', borderRadius: '20px', 
        fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '0.5px'
      }}>MOST POPULAR</span>
    )}
    
    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '10px', color: theme.secondary }}>{tier}</h3>
    <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '20px', minHeight: '40px', lineHeight: '1.5' }}>{subtext}</p>
    
    <div style={{ marginBottom: '5px' }}>
      <span style={{ fontSize: '3rem', fontWeight: '800', color: theme.secondary }}>£{price}</span>
      <span style={{ color: '#64748b', fontWeight: '500' }}>/mo</span>
    </div>

    {/* Expansion Text */}
    <div style={{ fontSize: '0.85rem', color: theme.primary, fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '5px' }}>
        {expansionText && <><Plus size={14} strokeWidth={3}/> {expansionText}</>}
    </div>
    
    <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '30px', fontStyle: 'italic' }}>
        billed {billingCycle}
    </div>

    <button 
      onClick={action.handler}
      style={{ 
        width: '100%', padding: '16px', borderRadius: '8px', border: 'none', 
        background: isPopular ? theme.primary : '#f1f5f9', 
        color: isPopular ? 'white' : theme.secondary, 
        fontWeight: 'bold', cursor: 'pointer', marginBottom: '30px',
        transition: 'all 0.2s',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '1rem'
    }}>
        {action.icon} {action.label}
    </button>
    
    <div style={{ borderTop: '1px solid #e2e8f0', margin: '0 -40px 30px', padding: '0 40px' }}></div>

    <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
      {features.map((f, i) => (
        <li key={i} style={{ display: 'flex', gap: '12px', marginBottom: '16px', fontSize: '0.95rem', color: '#475569', lineHeight: '1.5' }}>
          <div style={{ background: '#ecfdf5', borderRadius: '50%', padding: '2px', height: 'fit-content' }}>
             <Check size={16} color={theme.success || '#10b981'} strokeWidth={3} />
          </div>
          {f}
        </li>
      ))}
    </ul>
  </div>
);

const Pricing = ({ theme }) => {
  const [isYearly, setIsYearly] = useState(true);
  //const navigate = useNavigate(); 

  // UPDATED LOGIC: Trigger emails instead of navigating
  const handleActionLogic = (tier, cycle) => {
    if (tier === 'Enterprise') {
        return {
            label: "Request Quote",
            icon: <FileText size={18} />,
            // handler: () => navigate(`/register?plan=${tier.toLowerCase()}&billing=${cycle}&mode=quote`)
            handler: () => { window.location.href = 'mailto:sales@siteact.app'; }
        };
    }
    return {
        label: "Start Free Trial",
        icon: <Check size={18} />,
        // handler: () => navigate(`/register?plan=${tier.toLowerCase()}&billing=${cycle}&mode=trial`)
        handler: () => { window.location.href = 'mailto:sales@siteact.app'; }
    };
  };

  const labelStyle = (active) => ({ 
      fontSize: '1rem', fontWeight: active ? 'bold' : '500', 
      color: active ? theme.secondary : '#94a3b8', cursor: 'pointer', transition: 'color 0.2s' 
  });
  
  return (
    <section id="pricing" style={{ padding: '100px 20px', background: '#f8fafc' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '20px', color: theme.secondary }}>Flexible Site Pricing</h2>
          <p style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '40px' }}>
            Unlimited users. Unlimited storage. Scale your site count up or down anytime.
          </p>

          {/* Toggle Switch */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
            <span onClick={() => setIsYearly(false)} style={labelStyle(!isYearly)}>Monthly</span>
            <div 
                onClick={() => setIsYearly(!isYearly)} 
                style={{ 
                    width: '56px', height: '30px', background: theme.primary, 
                    borderRadius: '20px', position: 'relative', cursor: 'pointer', 
                    transition: 'background 0.3s', padding: '3px'
                }}
            >
              <div style={{ 
                  width: '24px', height: '24px', background: 'white', 
                  borderRadius: '50%', position: 'absolute', 
                  top: '3px', left: isYearly ? '29px' : '3px', 
                  transition: 'left 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </div>
            <span onClick={() => setIsYearly(true)} style={labelStyle(isYearly)}>
              Yearly <span style={{ color: theme.success || '#10b981', fontSize: '0.85rem', fontWeight: 'bold' }}>(Save 10%)</span>
            </span>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px', alignItems: 'start' }}>
          
          {/* CORE - £350 */}
          <PricingCard 
            theme={theme} tier="Core" price={isYearly ? "350" : "390"}
            billingCycle={isYearly ? 'yearly' : 'monthly'}
            action={handleActionLogic('Core', isYearly ? 'yearly' : 'monthly')}
            subtext="For boutique firms managing a single active project."
            expansionText={null} 
            features={['1 Active Site', 'Unlimited Users', 'All Features Included', 'Email Support']}
          />

          {/* PRO - £900 */}
          <PricingCard 
            theme={theme} tier="Pro" price={isYearly ? "900" : "1,000"} 
            billingCycle={isYearly ? 'yearly' : 'monthly'} isPopular={true}
            action={handleActionLogic('Pro', isYearly ? 'yearly' : 'monthly')}
            subtext="For growing contractors with multiple active jobs."
            expansionText="Includes 3 Sites. Add more for £250/site." 
            features={['Up to 3 Active Sites', 'Everything in Core',  'Priority Support', ]}
          />

          {/* ENTERPRISE - £1,950 */}
          <PricingCard 
            theme={theme} tier="Enterprise" price={isYearly ? "1,950" : "2,250"}
            billingCycle={isYearly ? 'yearly' : 'monthly'}
            action={handleActionLogic('Enterprise', isYearly ? 'yearly' : 'monthly')}
            subtext="For manufacturers & large contractors requiring standardization."
            expansionText="Includes 10 Sites. Add more for £175/site."
            features={['Up to 10 Active Sites', 'Everything in Pro', 'Dedicated Success Manager', 'SSO on Request']}
          />

        </div>

        <p style={{ textAlign: 'center', marginTop: '50px', color: '#94a3b8', fontSize: '0.9rem', fontStyle: 'italic' }}>
          * Prices exclude VAT. Expansion sites are billed at the pro-rated monthly rate.
        </p>
      </div>
    </section>
  );
};

export default Pricing;